// Turning one cpass reply into an APIResponse the screens can act on.
//
// The campaign screens read `{ success, data, error }`. Two things about cpass
// make that more than `res.data`:
//
//   1. Not every failure is a non-2xx. CampaignController raises, so its
//      failures arrive as 500s — but the neighbouring VoiceCampaignController
//      returns `ApiResponse.error(...)` as the BODY of an ok response, so the
//      reply is HTTP 200 carrying {"success":false,"message":"…"} and no
//      "error" key at all. A reader that trusts the status code reports
//      "Campaign paused successfully" for a campaign that was never paused. A
//      reader that trusts `success` but looks only at `error` gets the generic
//      fallback and drops what the backend actually said.
//
//   2. The success envelope nests its payload: {"success":true,"message":"…",
//      "data":[…]}. A delete puts null there. The old bare-fetch helper did
//      `data.data || data`, so a null payload handed the caller the envelope
//      itself — `{success,message,data}` posing as the resource.
//
// Both are decided here, once, so each call site stays a one-liner.
import type { APIResponse } from '../types/ivr';

/** The human-readable half of an error body, whichever key it landed in. */
const messageIn = (body: any): string | undefined => {
  // `message` first: on a Spring error body `error` is the generic reason
  // phrase ("Internal Server Error") while `message` — when present — names
  // the actual problem. On a cpass envelope only `message` exists.
  for (const v of [body?.message, body?.error]) {
    if (typeof v === 'string' && v.trim()) return v;
  }
  return undefined;
};

/**
 * A 2xx body, as APIResponse.
 *
 * @param body the axios response body (`res.data`)
 */
export const responseFrom = <T>(body: any): APIResponse<T> => {
  // 204s and plain-text bodies have no envelope to read.
  if (body === null || typeof body !== 'object') {
    return { success: true, data: body as T };
  }

  // 200-but-failed. Promote the message into `error`, because that is the
  // field every caller renders.
  if (body.success === false) {
    return { success: false, error: messageIn(body) || 'Request failed' };
  }

  return {
    success: true,
    data: ('data' in body ? body.data : body) as T,
    message: typeof body.message === 'string' ? body.message : undefined,
  };
};

/**
 * A thrown axios error, as APIResponse.
 *
 * @param fallback what to say when neither the body nor axios explains itself
 */
export const errorFrom = <T>(e: any, fallback: string): APIResponse<T> => {
  const body = e?.response?.data;
  const said = typeof body === 'string' && body.trim() ? body : messageIn(body);
  return { success: false, error: said || e?.message || fallback };
};
